import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

// Tour steps for different user types
const TOUR_STEPS = {
  user: [
    {
      target: '[data-tour="dashboard"]',
      title: 'Welcome to TheRentPayer!',
      content: 'This is your dashboard where you can see an overview of your policies, claims, and coverage status.',
      position: 'bottom'
    },
    {
      target: '[data-tour="get-quote"]',
      title: 'Get a Quote',
      content: 'Start here to get a quote for rent insurance. Enter your monthly rent and we\'ll calculate your premium.',
      position: 'bottom'
    },
    {
      target: '[data-tour="policies"]',
      title: 'Your Policies',
      content: 'View and manage your active insurance policies. You can download contracts and see coverage details.',
      position: 'bottom'
    },
    {
      target: '[data-tour="claims"]',
      title: 'File a Claim',
      content: 'If you experience job loss, file a claim here. Remember, you need 12 months of payments before you can file.',
      position: 'bottom'
    },
    {
      target: '[data-tour="wallet"]',
      title: 'Your Wallet',
      content: 'Claim payments and referral rewards are deposited here. You can withdraw funds to your bank account.',
      position: 'left'
    },
    {
      target: '[data-tour="referrals"]',
      title: 'Refer Friends',
      content: 'Share your referral link and earn $10 for each friend who signs up. They get 20% off too!',
      position: 'bottom'
    },
    {
      target: '[data-tour="support"]',
      title: 'Get Help',
      content: 'Need assistance? Visit our FAQ, Knowledge Base, or create a support ticket. Our AI chat is also available 24/7.',
      position: 'left'
    },
    {
      target: '[data-tour="profile"]',
      title: 'Your Profile',
      content: 'Update your personal information, notification preferences, and manage your account settings.',
      position: 'left'
    }
  ],
  agent: [
    {
      target: '[data-tour="agent-dashboard"]',
      title: 'Welcome to the Agent Portal!',
      content: 'This is your command center. See key metrics, pending tasks, and recent activity at a glance.',
      position: 'bottom'
    },
    {
      target: '[data-tour="sidebar-search"]',
      title: 'Quick Search',
      content: 'Use this search box to quickly find any menu item. Just start typing!',
      position: 'right'
    },
    {
      target: '[data-tour="favorites"]',
      title: 'Favorites',
      content: 'Hover over any menu item and click the star to add it to your favorites for quick access.',
      position: 'right'
    },
    {
      target: '[data-tour="crm-section"]',
      title: 'CRM Section',
      content: 'Manage quotes, claims, customers, contracts, and e-signatures all in one place.',
      position: 'right'
    },
    {
      target: '[data-tour="quotes"]',
      title: 'Quotes Management',
      content: 'Review pending quotes, approve or modify them, and convert them to active policies.',
      position: 'right'
    },
    {
      target: '[data-tour="claims"]',
      title: 'Claims Processing',
      content: 'Process customer claims, review documentation, and issue payments to approved claims.',
      position: 'right'
    },
    {
      target: '[data-tour="customers"]',
      title: 'Customer Management',
      content: 'View customer profiles, policies, documents, and communication history.',
      position: 'right'
    },
    {
      target: '[data-tour="finance-section"]',
      title: 'Finance Section',
      content: 'Track payments, process withdrawals, manage commissions, and view referral stats.',
      position: 'right'
    },
    {
      target: '[data-tour="team-section"]',
      title: 'Team Management',
      content: 'Add team members, manage roles, track online status, and view time logs.',
      position: 'right'
    },
    {
      target: '[data-tour="support-tickets"]',
      title: 'Support Tickets',
      content: 'Respond to customer inquiries, track ticket status, and convert solutions to knowledge base articles.',
      position: 'right'
    },
    {
      target: '[data-tour="settings"]',
      title: 'Settings',
      content: 'Configure Stripe, session timeouts, loyalty programs, and other system settings.',
      position: 'right'
    },
    {
      target: '[data-tour="notifications"]',
      title: 'Notifications',
      content: 'Stay updated with real-time notifications about new quotes, claims, and customer activity.',
      position: 'bottom'
    }
  ],
  // Role-specific additional steps
  claims_processor: [
    {
      target: '[data-tour="claims"]',
      title: 'Your Primary Focus',
      content: 'As a Claims Processor, this is where you\'ll spend most of your time. Review claims, verify documentation, and process payments.',
      position: 'right'
    }
  ],
  manager: [
    {
      target: '[data-tour="analytics"]',
      title: 'Analytics Dashboard',
      content: 'Monitor team performance, revenue trends, and key business metrics.',
      position: 'right'
    },
    {
      target: '[data-tour="team-status"]',
      title: 'Team Status',
      content: 'Track who\'s online, clock-in times, and export time reports for payroll.',
      position: 'right'
    }
  ]
};

// Tooltip component
function Tooltip({ step, currentStep, totalSteps, onNext, onPrev, onClose, position }) {
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});

  useEffect(() => {
    const targetEl = document.querySelector(step.target);
    if (!targetEl) {
      // If target not found, show centered
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10001
      });
      return;
    }

    const rect = targetEl.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const padding = 16;

    let style = { position: 'fixed', zIndex: 10001 };
    let arrow = {};

    switch (step.position) {
      case 'bottom':
        style.top = rect.bottom + padding;
        style.left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrow = { top: -8, left: '50%', transform: 'translateX(-50%) rotate(45deg)' };
        break;
      case 'top':
        style.top = rect.top - tooltipHeight - padding;
        style.left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrow = { bottom: -8, left: '50%', transform: 'translateX(-50%) rotate(45deg)' };
        break;
      case 'left':
        style.top = rect.top + rect.height / 2 - tooltipHeight / 2;
        style.left = rect.left - tooltipWidth - padding;
        arrow = { right: -8, top: '50%', transform: 'translateY(-50%) rotate(45deg)' };
        break;
      case 'right':
        style.top = rect.top + rect.height / 2 - tooltipHeight / 2;
        style.left = rect.right + padding;
        arrow = { left: -8, top: '50%', transform: 'translateY(-50%) rotate(45deg)' };
        break;
      default:
        style.top = rect.bottom + padding;
        style.left = rect.left;
    }

    // Keep within viewport
    if (style.left < padding) style.left = padding;
    if (style.left + tooltipWidth > window.innerWidth - padding) {
      style.left = window.innerWidth - tooltipWidth - padding;
    }
    if (style.top < padding) style.top = padding;
    if (style.top + tooltipHeight > window.innerHeight - padding) {
      style.top = window.innerHeight - tooltipHeight - padding;
    }

    setTooltipStyle(style);
    setArrowStyle(arrow);

    // Highlight target element
    targetEl.style.position = 'relative';
    targetEl.style.zIndex = '10000';
    targetEl.classList.add('tour-highlight');

    return () => {
      targetEl.style.zIndex = '';
      targetEl.classList.remove('tour-highlight');
    };
  }, [step]);

  return (
    <div 
      style={tooltipStyle}
      className="w-80 bg-card rounded-xl shadow-2xl border border-primary/20 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
    >
      {/* Arrow */}
      <div 
        style={arrowStyle}
        className="absolute w-4 h-4 bg-card border-l border-t border-primary/20"
      />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-white" />
          <span className="text-white font-medium text-sm">
            Step {currentStep + 1} of {totalSteps}
          </span>
        </div>
        <button 
          onClick={onClose}
          className="text-white/80 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-lg text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
          {step.title}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {step.content}
        </p>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2">
        <div className="h-1 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="px-4 pb-4 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrev}
          disabled={currentStep === 0}
          className="text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        
        {currentStep === totalSteps - 1 ? (
          <Button size="sm" onClick={onClose}>
            Finish Tour
          </Button>
        ) : (
          <Button size="sm" onClick={onNext}>
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Main OnboardingTour component
export function OnboardingTour({ userType = 'user', userRole = null, forceShow = false, onComplete }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);

  // Get tour steps based on user type and role
  useEffect(() => {
    let tourSteps = [...(TOUR_STEPS[userType] || TOUR_STEPS.user)];
    
    // Add role-specific steps for agents
    if (userType === 'agent' && userRole && TOUR_STEPS[userRole]) {
      tourSteps = [...tourSteps, ...TOUR_STEPS[userRole]];
    }
    
    setSteps(tourSteps);
  }, [userType, userRole]);

  // Check if tour should show automatically
  useEffect(() => {
    if (forceShow) {
      setIsActive(true);
      setCurrentStep(0);
      return;
    }

    const tourKey = `therentpayer_tour_completed_${userType}`;
    const hasCompletedTour = localStorage.getItem(tourKey);
    
    if (!hasCompletedTour && steps.length > 0) {
      // Small delay to let the page render
      const timer = setTimeout(() => {
        setIsActive(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userType, steps, forceShow]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, steps.length]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleClose = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    
    // Mark tour as completed
    const tourKey = `therentpayer_tour_completed_${userType}`;
    localStorage.setItem(tourKey, 'true');
    
    if (onComplete) {
      onComplete();
    }
  }, [userType, onComplete]);

  // Start tour on demand
  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  if (!isActive || steps.length === 0) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9999] animate-in fade-in duration-200"
        onClick={handleClose}
      />
      
      {/* Tooltip */}
      <Tooltip
        step={steps[currentStep]}
        currentStep={currentStep}
        totalSteps={steps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onClose={handleClose}
      />
    </>
  );
}

// Hook to start tour on demand
export function useOnboardingTour(userType = 'user') {
  const [showTour, setShowTour] = useState(false);

  const startTour = useCallback(() => {
    setShowTour(true);
  }, []);

  const endTour = useCallback(() => {
    setShowTour(false);
  }, []);

  return { showTour, startTour, endTour };
}

// Help menu button component
export function TourHelpButton({ userType = 'user', userRole = null }) {
  const [showTour, setShowTour] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const startTour = () => {
    setShowMenu(false);
    // Clear the completion flag to allow re-taking the tour
    const tourKey = `therentpayer_tour_completed_${userType}`;
    localStorage.removeItem(tourKey);
    setShowTour(true);
  };

  return (
    <>
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowMenu(!showMenu)}
          className="flex items-center gap-2"
          data-testid="help-menu-button"
        >
          <HelpCircle className="h-5 w-5" />
          <span className="hidden md:inline">Help</span>
        </Button>

        {showMenu && (
          <>
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 top-full mt-2 w-56 bg-card rounded-lg shadow-lg border z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <button
                onClick={startTour}
                className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-3 text-sm"
                data-testid="take-tour-button"
              >
                <Sparkles className="h-4 w-4 text-primary" />
                <div>
                  <p className="font-medium">Take a Tour</p>
                  <p className="text-xs text-muted-foreground">Learn how to use the portal</p>
                </div>
              </button>
              <a
                href="/faq"
                className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-3 text-sm"
              >
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">FAQs</p>
                  <p className="text-xs text-muted-foreground">Common questions answered</p>
                </div>
              </a>
              <a
                href="/knowledge-base"
                className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-3 text-sm"
              >
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">Knowledge Base</p>
                  <p className="text-xs text-muted-foreground">Detailed guides & articles</p>
                </div>
              </a>
              {userType === 'user' && (
                <a
                  href="/support"
                  className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-3 text-sm border-t mt-2 pt-2"
                >
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Contact Support</p>
                    <p className="text-xs text-muted-foreground">Create a support ticket</p>
                  </div>
                </a>
              )}
            </div>
          </>
        )}
      </div>

      {showTour && (
        <OnboardingTour
          userType={userType}
          userRole={userRole}
          forceShow={true}
          onComplete={() => setShowTour(false)}
        />
      )}
    </>
  );
}

export default OnboardingTour;
